/**
 * Cliente para a API da loja (chefia/api/v1/*.php), servida em /api no
 * mesmo dominio do site — cookie de sessao do carrinho e first-party.
 */

export interface Produto {
  id: number;
  slug: string;
  nome: string;
  sku: string | null;
  descricaoCurta: string | null;
  descricao?: string | null;
  preco: number;
  precoDe: number | null;
  estoque: number;
  disponivel: boolean;
  destaque: boolean;
  categoria: string | null;
  categoriaSlug: string | null;
  imagem: string | null;
  imagens?: string[];
  seo?: { title: string; description: string | null };
}

export interface Categoria {
  id: number;
  slug: string;
  nome: string;
  descricao: string | null;
  paiId: number | null;
  imagem: string | null;
  produtos: number;
}

export interface CartItem {
  id: number;
  produtoId: number;
  nome: string;
  slug: string;
  quantidade: number;
  precoUnit: number;
  total: number;
  imagem: string | null;
  indisponivel: boolean;
}

export interface Cart {
  itens: CartItem[];
  subtotal: number;
  frete: number;
  total: number;
}

export interface Paginacao {
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

export class ApiError extends Error {
  status: number;
  detalhes?: string[];
  constructor(message: string, status: number, detalhes?: string[]) {
    super(message);
    this.status = status;
    this.detalhes = detalhes;
  }
}

// No servidor (ex: produto/[slug]/layout.tsx, generateMetadata) fetch()
// precisa de URL absoluta - nao ha "documento atual" para resolver um
// caminho relativo, ao contrario do browser. Mesmo padrao de cases-api.ts/
// sitemap.ts. `credentials: 'include'` nao tem efeito no servidor (nao ha
// cookie jar do navegador ali) - inofensivo deixar como esta para as
// chamadas de carrinho, que so acontecem client-side de qualquer forma.
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://enqdigital.com.br';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const url = typeof window === 'undefined' ? `${SITE_URL}${path}` : path;
  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new ApiError(
      data.error || 'Nao foi possivel completar a operacao.',
      res.status,
      data.detalhes
    );
  }

  return data as T;
}

export function listarProdutos(params: {
  categoria?: string;
  busca?: string;
  destaque?: boolean;
  pagina?: number;
  limite?: number;
  ordem?: 'preco_asc' | 'preco_desc' | 'nome';
  locale?: string;
}): Promise<{ produtos: Produto[]; paginacao: Paginacao }> {
  const qs = new URLSearchParams();
  if (params.categoria) qs.set('categoria', params.categoria);
  if (params.busca) qs.set('busca', params.busca);
  if (params.destaque) qs.set('destaque', '1');
  if (params.pagina) qs.set('pagina', String(params.pagina));
  if (params.limite) qs.set('limite', String(params.limite));
  if (params.ordem) qs.set('ordem', params.ordem);
  if (params.locale) qs.set('locale', params.locale);
  return call(`/api/v1/produtos.php?${qs.toString()}`);
}

export function buscarProduto(slug: string, locale?: string): Promise<{ produto: Produto }> {
  const qs = new URLSearchParams({ slug });
  if (locale) qs.set('locale', locale);
  return call(`/api/v1/produtos.php?${qs.toString()}`);
}

export function listarCategorias(locale?: string): Promise<{ categorias: Categoria[] }> {
  const qs = locale ? `?locale=${encodeURIComponent(locale)}` : '';
  return call(`/api/v1/categorias.php${qs}`);
}

export function verCarrinho(): Promise<{ carrinho: Cart }> {
  return call('/api/v1/carrinho.php');
}

export function adicionarAoCarrinho(
  produtoId: number,
  quantidade = 1
): Promise<{ carrinho: Cart }> {
  return call('/api/v1/carrinho.php', {
    method: 'POST',
    body: JSON.stringify({ produtoId, quantidade }),
  });
}

export function alterarQuantidade(itemId: number, quantidade: number): Promise<{ carrinho: Cart }> {
  return call('/api/v1/carrinho.php', {
    method: 'PATCH',
    body: JSON.stringify({ itemId, quantidade }),
  });
}

export function removerDoCarrinho(itemId: number): Promise<{ carrinho: Cart }> {
  return call(`/api/v1/carrinho.php?itemId=${itemId}`, { method: 'DELETE' });
}

export interface EnderecoCliente {
  id: number;
  rotulo: string | null;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string | null;
  bairro: string;
  cidade: string;
  uf: string;
  padrao: boolean;
}

export interface Cliente {
  id: number;
  nome: string;
  email: string;
  telefone: string | null;
  documento: string | null;
  empresa: string | null;
  emailVerificado: boolean;
  enderecos?: EnderecoCliente[];
}

/** Dados do cliente logado (sessao via cookie ENQ_STORE), ou null se nao ha sessao. */
export function buscarConta(): Promise<{ cliente: Cliente | null }> {
  return call('/api/v1/conta.php');
}

export function logoutConta(): Promise<{ ok: boolean }> {
  return call('/api/v1/conta.php?acao=logout', { method: 'POST' });
}

/**
 * URL que inicia o login/cadastro de cliente via Google (redirect de
 * navegador, nao fetch - troca code/id_token no backend e cria sessao
 * real via cookie). `returnTo` e o path para onde o Google devolve o
 * visitante depois, com `?login_google=ok|negado|cancelado|...` na query.
 */
export function urlLoginGoogle(returnTo: string): string {
  return `/api/oauth_google_customer_start.php?return_to=${encodeURIComponent(returnTo)}`;
}

export function enviarSolicitacao(dados: {
  nome: string;
  email: string;
  telefone?: string;
  documento?: string;
  empresa?: string;
  mensagem?: string;
}): Promise<{ solicitacao: { id: number; mensagem: string; itens: number } }> {
  return call('/api/v1/solicitacao.php', {
    method: 'POST',
    body: JSON.stringify(dados),
  });
}

export function money(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Todo o catálogo hoje é infraestrutura por assinatura (cloud/baremetal): preço é sempre mensal. */
export function moneyMensal(value: number): string {
  return `${money(value)}/mês`;
}
